import type { CustomFieldDefinition } from "@repo/app-types/entities/customField";
import { Searcher } from "fast-fuzzy";

/** Static fields that are assigned/mapped to the columns in a CSV file. */
export const SourceField = {
  FirstName: "first-name",
  LastName: "last-name",
  FullName: "full-name",
  Email: "email",
  Phone: "phone",
  ProjectId: "project-id",
  ProjectName: "project-name",
  JobTitle: "job-title",
  JobLocation: "job-location",
} as const;

export const projectSourceFields = [
  SourceField.ProjectId,
  SourceField.ProjectName,
  SourceField.JobTitle,
  SourceField.JobLocation,
];

export const staticSourceFields = [
  SourceField.FirstName,
  SourceField.LastName,
  SourceField.FullName,
  SourceField.Email,
  SourceField.Phone,
  ...projectSourceFields,
] as const;

export type StaticSourceField = (typeof staticSourceFields)[number];

export function checkIsStaticSourceField(
  field: string,
): field is StaticSourceField {
  return staticSourceFields.some((staticField) => staticField === field);
}

/** Prefixes for dynamic fields that are assigned/mapped to the columns in a CSV file. */
export const SourceFieldPrefix = {
  CustomField: "custom-field",
} as const;

export const dynamicSourceFieldPrefixes = [
  SourceFieldPrefix.CustomField,
] as const;

type DynamicSourceFieldPrefix = (typeof dynamicSourceFieldPrefixes)[number];

export type DynamicSourceField = `${DynamicSourceFieldPrefix}:${string}`;

export function checkIsDynamicSourceField(
  field: string,
): field is DynamicSourceField {
  return dynamicSourceFieldPrefixes.some((prefix) =>
    field.startsWith(`${prefix}:`),
  );
}

export function parseDynamicSourceField(field: DynamicSourceField): {
  prefix: DynamicSourceFieldPrefix;
  id: string;
} {
  const [prefix, id] = field.split(":", 2);
  return { prefix: prefix as DynamicSourceFieldPrefix, id: id ?? "" };
}

export type SourceField = StaticSourceField | DynamicSourceField;

export function collectAllSourceFields(
  customFields: readonly CustomFieldDefinition[],
): SourceField[] {
  return [
    ...staticSourceFields,
    ...customFields.map(
      (customField): DynamicSourceField =>
        `${SourceFieldPrefix.CustomField}:${customField.id}`,
    ),
  ];
}

export function checkIsSourceField(field: string): field is SourceField {
  return checkIsStaticSourceField(field) || checkIsDynamicSourceField(field);
}

/** Assignment of column indexes to fields. */
export type SourceMapping = Partial<Record<SourceField, number>>;

/** Fields will be auto-assigned in this order by doing a fuzzy search on the CSV headers. */
const sourceFieldSearchTerms = [
  {
    field: SourceField.FirstName,
    searchTerms: ["first name", "given name", "forename"],
  },
  {
    field: SourceField.LastName,
    searchTerms: ["last name", "family name", "surname"],
  },
  {
    field: SourceField.FullName,
    searchTerms: ["full name"],
  },
  {
    field: SourceField.Email,
    searchTerms: ["email", "email address"],
  },
  {
    field: SourceField.Phone,
    searchTerms: ["phone", "phone number"],
  },
  {
    field: SourceField.ProjectId,
    searchTerms: ["id", "project id", "job opening id", "training id"],
  },
  {
    field: SourceField.ProjectName,
    searchTerms: [
      "project",
      "project name",
      "opening",
      "job opening",
      "job opening name",
      "training",
      "training name",
    ],
  },
  {
    field: SourceField.JobTitle,
    searchTerms: ["job title", "role"],
  },
  {
    field: SourceField.JobLocation,
    searchTerms: ["location", "job location"],
  },
];

export function generateSourceMapping(
  header: readonly string[],
  ignoredColumnIndexes: Set<number> = new Set(),
): SourceMapping {
  const mapping: SourceMapping = {};
  const mappedColumnIndexes = new Set<number>();

  const searcher = new Searcher(
    header
      .map((label, index) => ({
        index,
        label,
      }))
      .filter(({ index }) => !ignoredColumnIndexes.has(index)),
    {
      keySelector: (column) => column.label,
      threshold: 0.8,
      // No substring matching.
      useSellers: false,
    },
  );

  for (const { field, searchTerms } of sourceFieldSearchTerms) {
    // Skip fields that have already been mapped.
    if (mapping[field] !== undefined) {
      continue;
    }

    for (const searchTerm of searchTerms) {
      const match = searcher
        .search(searchTerm, {
          returnMatchData: true,
        })
        // Remove columns that have already been mapped.
        .filter((match) => !mappedColumnIndexes.has(match.item.index))
        .at(0);

      if (match) {
        mapping[field] = match.item.index;
        mappedColumnIndexes.add(match.item.index);
        break;
      }
    }
  }

  return mapping;
}

export function getMappedSourceFields(mapping: SourceMapping): SourceField[] {
  return Object.keys(mapping).filter(checkIsSourceField);
}

export function getSourceFieldForColumnIndex(
  mapping: SourceMapping,
  columnIndex: number,
): SourceField | null {
  return (
    getMappedSourceFields(mapping).find(
      (field) => mapping[field] === columnIndex,
    ) ?? null
  );
}

export function setSourceFieldForColumnIndex(
  mapping: SourceMapping,
  columnIndex: number,
  field: SourceField | null,
): SourceMapping {
  const nextMapping = { ...mapping };

  for (const sourceField of getMappedSourceFields(mapping)) {
    if (nextMapping[sourceField] === columnIndex) {
      delete nextMapping[sourceField];
    }
  }

  if (field !== null) {
    nextMapping[field] = columnIndex;
  }

  return nextMapping;
}
